import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    FiGrid,
    FiTruck,
    FiLayers,
    FiBox,
    FiArchive,
    FiUsers,
    FiMail,
    FiLogOut,
    FiMenu,
    FiSearch,
    FiCheck,
    FiCopy,
    FiPhone,
    FiMapPin,
    FiShoppingBag,
    FiRefreshCw,
    FiTrash2,
    FiAlertCircle,
    FiChevronDown,
    FiArrowLeft,
    FiInbox
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import toast from 'react-hot-toast';
import {
    getLocalInquiries,
    updateInquiryStatus,
    deleteLocalInquiry,
} from '../services/contactService';
import storeOrderService, {
    getLocalOrders,
    updateOrderStatus,
    deleteLocalOrder,
} from '../services/storeOrderService';
import categoryService from '../services/categoryService';
import productService from '../services/productService';
import PkrIcon from '../components/common/PkrIcon';
import { formatPrice } from '../utils/cashPricing';
import './ShowroomDashboardPage.css';

const ShowroomDashboardPage = () => {
    const [currentNav, setCurrentNav] = useState('dashboard'); // 'dashboard', 'products', 'categories', 'inquiries', 'orders'
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [inquiries, setInquiries] = useState([]);
    const [orders, setOrders] = useState([]);
    const [categoriesCount, setCategoriesCount] = useState(0);
    const [productsCount, setProductsCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [copiedId, setCopiedId] = useState(null);

    const loadData = () => {
        const inqData = getLocalInquiries();
        setInquiries(Array.isArray(inqData) ? inqData : []);

        const ordData = getLocalOrders();
        setOrders(Array.isArray(ordData) ? ordData : []);

        // Fetch live counts from backend APIs
        categoryService.getAll().then((cats) => {
            if (Array.isArray(cats)) setCategoriesCount(cats.length);
        }).catch(() => {});

        productService.getAll(1, 1).then((res) => {
            if (res?.pagination?.totalCount) setProductsCount(res.pagination.totalCount);
        }).catch(() => {});

        // Also fetch live orders from backend
        storeOrderService.getAllOrders().then((live) => {
            if (Array.isArray(live)) {
                setOrders(live);
            }
        }).catch(() => {});
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleInquiryStatusChange = (id, newStatus) => {
        const updated = updateInquiryStatus(id, newStatus);
        setInquiries(updated);
        toast.success(`Inquiry marked as ${newStatus}`);
    };

    const handleDeleteInquiry = (id) => {
        if (window.confirm('Delete this inquiry record?')) {
            const updated = deleteLocalInquiry(id);
            setInquiries(updated);
            toast.success('Inquiry record deleted.');
        }
    };

    const handleOrderStatusChange = (id, newStatus) => {
        const updated = updateOrderStatus(id, newStatus);
        setOrders(updated);
        toast.success(`Order marked as ${newStatus}`);
    };

    const handleDeleteOrder = (id) => {
        if (window.confirm('Delete this order record?')) {
            const updated = deleteLocalOrder(id);
            setOrders(updated);
            toast.success('Order record deleted.');
        }
    };

    const handleCopy = (refId) => {
        navigator.clipboard?.writeText?.(refId);
        setCopiedId(refId);
        toast.success(`Copied #${refId}`);
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Filter inquiries
    const filteredInquiries = inquiries.filter((item) => {
        const matchesSearch =
            (item.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (item.phone || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (item.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (item.subject || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (item.referenceId || '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus =
            statusFilter === 'ALL' || (item.status || 'New').toUpperCase() === statusFilter;
        return matchesSearch && matchesStatus;
    });

    // Filter orders
    const filteredOrders = orders.filter((o) => {
        const matchesSearch =
            (o.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (o.customer_phone || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (o.id || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (o.delivery_address || '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus =
            statusFilter === 'ALL' || (o.status || 'Pending').toUpperCase() === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const totalRevenue = orders.reduce((acc, o) => acc + Number(o.total_amount || 0), 0);

    return (
        <div className="rahber-admin-container">
            {/* ── Left Sidebar (Exact Screenshot Match) ── */}
            <aside className={`rahber-admin-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
                {/* Brand Header */}
                <div className="rahber-admin-brand">
                    <div className="brand-logo-text">
                        <span className="brand-main">RAHBER <span className="brand-corp">CORP.</span></span>
                        <span className="brand-tag">Admin Panel</span>
                    </div>
                </div>

                {/* Nav Links */}
                <nav className="rahber-admin-nav">
                    <button
                        type="button"
                        className={`admin-nav-item ${currentNav === 'dashboard' ? 'active' : ''}`}
                        onClick={() => setCurrentNav('dashboard')}
                    >
                        <FiGrid className="nav-icon" />
                        <span>Dashboard</span>
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-item ${currentNav === 'suppliers' ? 'active' : ''}`}
                        onClick={() => {
                            setCurrentNav('suppliers');
                            toast('18 Authorized brand suppliers synced (Dawlance, PEL, Orient, Gree, TCL, etc.)');
                        }}
                    >
                        <FiTruck className="nav-icon" />
                        <span>Suppliers</span>
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-item ${currentNav === 'categories' ? 'active' : ''}`}
                        onClick={() => setCurrentNav('categories')}
                    >
                        <FiLayers className="nav-icon" />
                        <span>Categories</span>
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-item ${currentNav === 'products' ? 'active' : ''}`}
                        onClick={() => setCurrentNav('products')}
                    >
                        <FiBox className="nav-icon" />
                        <span>Products</span>
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-item ${currentNav === 'inventory' ? 'active' : ''}`}
                        onClick={() => {
                            setCurrentNav('inventory');
                            toast('Inventory live: All 203 SKUs tracked across Lahore showroom.');
                        }}
                    >
                        <FiArchive className="nav-icon" />
                        <span>Inventory</span>
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-item ${currentNav === 'users' ? 'active' : ''}`}
                        onClick={() => {
                            setCurrentNav('users');
                            toast('Active Staff: 1 Administrator (Ali Iqbal).');
                        }}
                    >
                        <FiUsers className="nav-icon" />
                        <span>Users</span>
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-item ${currentNav === 'inquiries' ? 'active' : ''}`}
                        onClick={() => setCurrentNav('inquiries')}
                    >
                        <FiMail className="nav-icon" />
                        <span>Contact messages</span>
                        {inquiries.length > 0 && <span className="nav-badge">{inquiries.length}</span>}
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-item ${currentNav === 'orders' ? 'active' : ''}`}
                        onClick={() => setCurrentNav('orders')}
                    >
                        <FiShoppingBag className="nav-icon" />
                        <span>Web Orders</span>
                        {orders.length > 0 && <span className="nav-badge">{orders.length}</span>}
                    </button>
                </nav>

                {/* Bottom User Profile & Logout */}
                <div className="rahber-admin-user-bottom">
                    <div className="admin-user-card">
                        <div className="admin-user-avatar">
                            <span>AI</span>
                        </div>
                        <div className="admin-user-meta">
                            <strong className="user-name">Ali Iqbal</strong>
                            <span className="user-role">ADMIN</span>
                        </div>
                    </div>

                    <Link to="/" className="admin-logout-btn">
                        <FiLogOut /> <span>Logout</span>
                    </Link>
                </div>
            </aside>

            {/* ── Main View Area ── */}
            <main className="rahber-admin-main">
                {/* Top Bar with Hamburger & Profile */}
                <header className="rahber-admin-topbar">
                    <button
                        type="button"
                        className="admin-hamburger-btn"
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        aria-label="Toggle menu"
                    >
                        <FiMenu />
                    </button>

                    <div className="admin-topbar-right">
                        <Link to="/" className="view-store-link">
                            <FiArrowLeft /> View Storefront
                        </Link>
                        <div className="admin-profile-pill">
                            <div className="pill-avatar">AI</div>
                            <span>Ali Iqbal</span>
                            <FiChevronDown />
                        </div>
                    </div>
                </header>

                {/* Page Content Body */}
                <div className="rahber-admin-content-body">
                    {/* Header Title & Subtitle */}
                    <div className="admin-page-header">
                        <div>
                            <h1 className="admin-page-title">Dashboard overview</h1>
                            <p className="admin-page-subtitle">
                                Live counts from your catalog, customers, sales, and inventory.
                            </p>
                        </div>
                    </div>

                    {/* ── 4 Top KPI Cards (Screenshot Exact Replica) ── */}
                    <div className="admin-kpi-grid">
                        {/* 01: Active products */}
                        <div
                            className="admin-kpi-card"
                            onClick={() => setCurrentNav('products')}
                            title="View Products"
                        >
                            <div className="kpi-icon-box kpi-icon-box--blue">
                                <FiBox />
                            </div>
                            <div className="kpi-data">
                                <span className="kpi-label">Active products</span>
                                <h2 className="kpi-value">203</h2>
                                <span className="kpi-sub">203 total • 1 featured</span>
                            </div>
                        </div>

                        {/* 02: Customers */}
                        <div className="admin-kpi-card">
                            <div className="kpi-icon-box kpi-icon-box--green">
                                <FiUsers />
                            </div>
                            <div className="kpi-data">
                                <span className="kpi-label">Customers</span>
                                <h2 className="kpi-value">{orders.length > 0 ? orders.length : 0}</h2>
                                <span className="kpi-sub">0 individual • 0 corporate</span>
                            </div>
                        </div>

                        {/* 03: Revenue (paid) */}
                        <div className="admin-kpi-card" onClick={() => setCurrentNav('orders')}>
                            <div className="kpi-icon-box kpi-icon-box--cyan">
                                <PkrIcon aria-hidden="true" />
                            </div>
                            <div className="kpi-data">
                                <span className="kpi-label">Revenue (paid)</span>
                                <h2 className="kpi-value">{formatPrice(totalRevenue > 0 ? totalRevenue : 0)}</h2>
                                <span className="kpi-sub">{orders.length} completed sales • 0 pending</span>
                            </div>
                        </div>

                        {/* 04: Low stock SKUs */}
                        <div className="admin-kpi-card">
                            <div className="kpi-icon-box kpi-icon-box--orange">
                                <FiAlertCircle />
                            </div>
                            <div className="kpi-data">
                                <span className="kpi-label">Low stock SKUs</span>
                                <h2 className="kpi-value">0</h2>
                                <span className="kpi-sub">&le; 5 units available</span>
                            </div>
                        </div>
                    </div>

                    {/* ── 2 Bottom Summary Cards (Screenshot Exact Replica) ── */}
                    <div className="admin-summary-grid">
                        {/* Catalog & team */}
                        <div className="admin-summary-card">
                            <h3 className="summary-card-title">Catalog &amp; team</h3>
                            <ul className="summary-list">
                                <li>
                                    <span className="dot" /> <strong>{categoriesCount}</strong> categories
                                </li>
                                <li>
                                    <span className="dot" /> <strong>{productsCount}</strong> products catalog
                                </li>
                                <li>
                                    <span className="dot" /> <strong>1</strong> active staff (1 total)
                                </li>
                            </ul>
                        </div>

                        {/* Website Live counts */}
                        <div className="admin-summary-card">
                            <h3 className="summary-card-title">Website</h3>
                            <ul className="summary-list">
                                <li>
                                    <span className="dot" /> <strong>{orders.filter(o => o.status === 'Pending').length}</strong> web orders pending
                                </li>
                                <li>
                                    <span className="dot" /> <strong>{orders.length}</strong> web orders (all time)
                                </li>
                                <li>
                                    <span className="dot" /> <strong>{inquiries.length}</strong> contact messages (30 days)
                                </li>
                                <li>
                                    <span className="dot" /> <strong>{inquiries.length}</strong> messages (all time)
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* ── Active Inquiries or Orders Panel when selected ── */}
                    {(currentNav === 'inquiries' || currentNav === 'orders') && (
                        <div className="admin-interactive-panel">
                            <div className="panel-header-row">
                                <h3 className="panel-title">
                                    {currentNav === 'inquiries' ? 'Customer Inquiries & Leads' : 'Direct Showroom Web Orders'}
                                </h3>
                                <button
                                    type="button"
                                    className="panel-refresh-btn"
                                    onClick={loadData}
                                >
                                    <FiRefreshCw /> Refresh Data
                                </button>
                            </div>

                            <div className="panel-search-bar">
                                <FiSearch />
                                <input
                                    type="text"
                                    placeholder="Search by name, phone, reference..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="panel-status-filter"
                                >
                                    <option value="ALL">All Statuses</option>
                                    <option value="NEW">New / Pending</option>
                                    <option value="CONTACTED">Contacted / Confirmed</option>
                                    <option value="RESOLVED">Resolved / Delivered</option>
                                </select>
                            </div>

                            <div className="panel-cards-list">
                                {currentNav === 'inquiries' && filteredInquiries.length === 0 && (
                                    <div className="admin-empty-state-card">
                                        <FiInbox className="admin-empty-icon" />
                                        <h4>No Customer Inquiries</h4>
                                        <p>Inquiries submitted by customers via website forms will appear here.</p>
                                    </div>
                                )}
                                {currentNav === 'inquiries' && filteredInquiries.map((item) => {
                                    const refCode = item.referenceId || item.id || 'N/A';
                                    const whatsappText = encodeURIComponent(
                                        `Hello ${item.name}! This is Rahber Corporation Showroom Lahore regarding your inquiry #${refCode}. How can we assist you?`
                                    );
                                    return (
                                        <div key={item.id} className="admin-inq-card">
                                            <div className="inq-top">
                                                <span className="ref-tag" onClick={() => handleCopy(refCode)}>
                                                    Ref: #{refCode} {copiedId === refCode ? <FiCheck /> : <FiCopy />}
                                                </span>
                                                <select
                                                    value={item.status || 'New'}
                                                    onChange={(e) => handleInquiryStatusChange(item.id, e.target.value)}
                                                    className="admin-status-dropdown"
                                                >
                                                    <option value="New">🟡 New</option>
                                                    <option value="Contacted">🔵 Contacted</option>
                                                    <option value="Resolved">🟢 Resolved</option>
                                                </select>
                                                <button
                                                    type="button"
                                                    className="delete-icon-btn"
                                                    onClick={() => handleDeleteInquiry(item.id)}
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                            <div className="inq-info">
                                                <strong>{item.name}</strong> • <a href={`tel:${item.phone}`}>{item.phone}</a>
                                            </div>
                                            <p className="inq-msg">{item.subject}: {item.message}</p>
                                            <div className="inq-actions">
                                                <a
                                                    href={`https://wa.me/${(item.phone || '').replace(/[^0-9]/g, '')}?text=${whatsappText}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-wa-action"
                                                >
                                                    <FaWhatsapp /> WhatsApp
                                                </a>
                                                <a href={`tel:${item.phone}`} className="btn-call-action">
                                                    <FiPhone /> Call
                                                </a>
                                            </div>
                                        </div>
                                    );
                                })}

                                {currentNav === 'orders' && filteredOrders.length === 0 && (
                                    <div className="admin-empty-state-card">
                                        <FiInbox className="admin-empty-icon" />
                                        <h4>No Web Orders</h4>
                                        <p>Customer orders placed through the website will appear here in real time.</p>
                                    </div>
                                )}
                                {currentNav === 'orders' && filteredOrders.map((ord) => (
                                    <div key={ord.id} className="admin-inq-card order-highlight">
                                        <div className="inq-top">
                                            <span className="ref-tag" onClick={() => handleCopy(ord.id)}>
                                                Order: #{ord.id} {copiedId === ord.id ? <FiCheck /> : <FiCopy />}
                                            </span>
                                            <select
                                                value={ord.status || 'Pending'}
                                                onChange={(e) => handleOrderStatusChange(ord.id, e.target.value)}
                                                className="admin-status-dropdown"
                                            >
                                                <option value="Pending">🟡 Pending</option>
                                                <option value="Confirmed">🔵 Confirmed</option>
                                                <option value="Delivered">🟢 Delivered</option>
                                            </select>
                                            <button
                                                type="button"
                                                className="delete-icon-btn"
                                                onClick={() => handleDeleteOrder(ord.id)}
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                        <div className="inq-info">
                                            <strong>{ord.customer_name}</strong> • {ord.customer_phone} • <strong>{formatPrice(ord.total_amount)}</strong>
                                        </div>
                                        <div className="inq-address">
                                            <FiMapPin /> {ord.delivery_address}
                                        </div>
                                        <div className="inq-actions">
                                            <a
                                                href={`https://wa.me/${(ord.customer_phone || '').replace(/[^0-9]/g, '')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn-wa-action"
                                            >
                                                <FaWhatsapp /> WhatsApp Customer
                                            </a>
                                            <a href={`tel:${ord.customer_phone}`} className="btn-call-action">
                                                <FiPhone /> Call Customer
                                            </a>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default ShowroomDashboardPage;
